import React, { useState, useEffect, useRef, Fragment } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Col, Container, Row } from 'reactstrap';
import { get, isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { PuffLoader } from 'react-spinners';
import { Button } from 'components/Button';
import easyIcon from '../../../assets/images/easy.png';
import stepsIcon from '../../../assets/images/steps.png';
import superfastIcon from '../../../assets/images/super-fast.png';
import fullScreenIcon from '../../../assets/images/arrow-button.png';
import { nextAction, getIPAddress } from 'actions/workflow';
import style from './style.scss';
import cn from 'classnames';
import Header from '../../../components/Header';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;

const EsignLoanDocs = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const workflow = useSelector((state) => state.workflow);
  const [searchParams] = useSearchParams();
  const [isChecked, setIsChecked] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageLoad, setPageLoad] = useState(false);
  const [ipAddress, setIpAddress] = useState(null);
  const pdfContainerRef = useRef(null);
  const key = searchParams.get('key');

  const base64ToArrayBuffer = (data) => {
    if (data) {
      const bytes = new Uint8Array(data.length);
      const arrayBuffer = bytes.map((item, idx) => data.charCodeAt(idx));
      return arrayBuffer;
    }
  };

  const renderAllPages = async (loadingTask) => {
    try {
      const pdf = await loadingTask.promise;
      const totalPages = pdf.numPages;

      const renderPromises = [];

      for (let pageNumber = 1; pageNumber <= totalPages; pageNumber++) {
        renderPromises.push(pdf.getPage(pageNumber).then((page) => {
          const PDFWidth = page.getViewport({ scale: 1 }).width;
          const scale = 610 / PDFWidth;
          const viewport = page.getViewport({ scale });
          const canvas = document.createElement('canvas');
          canvas.id = `pdf-page-${pageNumber}`;
          canvas.classList.add('pdf-page');
          const context = canvas.getContext('2d');
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          if (pdfContainerRef.current) {
            pdfContainerRef.current.appendChild(canvas);
          }

          const renderContext = {
            canvasContext: context,
            viewport,
          };
          return page.render(renderContext).promise;
        }));
      }

      await Promise.all(renderPromises);
      setLoading(false);
      setPageLoad(false);
    } catch (error) {
      setPageLoad(false);
      setLoading(false);
      console.error('Error rendering PDF pages:', error);
    }
  };

  const convertedPdfUrl = () => {
    const pdfData = window.atob(get(workflow, 'data.data.loanDocument'));
    const type = 'PDF';
    const arrayBuffer = base64ToArrayBuffer(pdfData);
    const file = new Blob([arrayBuffer], { type: `application/${type}` });
    const data = window.URL.createObjectURL(file);
    setPdfUrl(data);
    const loadingTask = pdfjsLib.getDocument({ data: pdfData });
    renderAllPages(loadingTask);
  };

  const getIPAddressAction = () => {
    dispatch(getIPAddress({
      data: {},
      success: (ipResponse) => {
        setIpAddress(ipResponse.ip);
      },
      fail: () => {
        console.log('Getting IP address failed');
      },
    }));
  };

  useEffect(() => {
    if (!key) {
      navigate(`/fsl/application${window.location.search}`);
    } else if (!workflow || isEmpty(workflow)) {
      navigate(`/applications/fsl/checkin?key=${key || ''}`);
    } else if (workflow) {
      convertedPdfUrl();
      getIPAddressAction();
    }
  }, []);

  const handleEsign = async () => {
    setLoading(true);
    dispatch(nextAction({
      data: {
        id: get(workflow, 'attributes.entityId'),
        error: null,
        IpAddress: ipAddress || 'static ip',
      },
      url: `/workflows/fsl/${key}/next`,
      success: (res) => {
        const routeUrl = get(res, 'data.url');
        navigate(routeUrl);
      },
      fail: (error) => {
        setLoading(false);
        console.log('error', error);
      },
    }));
  };

  const handleCheckboxChange = () => {
    setIsChecked(!isChecked);
  };

  return (
    <div>
      <Header />
      <Container fluid>
        <Row className={style.esignWrapper}>
          <Col sm={{ size: 12 }}>
            {get(workflow, 'attributes.entityId') && (
              <p className={style.applicationHeader}>
                <b>Application ID:</b> {get(workflow, 'attributes.entityId')}
              </p>
            )}
            {pageLoad ?
              <div style={{ textAlign: 'center', height: 352, lineHeight: '400px' }}>
                <PuffLoader
                  color="#008aea"
                  size={100}
                />
              </div>
            :
              <Fragment>
            <div className={style.pageHead}>
              <h1>You&apos;re almost there! Just click and go!</h1>
              <h4>Please electronically sign your documents</h4>
            </div>
            <div className={style.esignTextWrapper}>
              <p>
                By clicking the <strong>ACKNOWLEDGE</strong> button below and clicking{' '}
                <strong>ESIGN LOAN DOCUMENTS</strong>, you acknowledge that you
                have read and agree to the{' '}
                  <a className={style.esignLink} href={pdfUrl} target="_blank" rel="noopener noreferrer">
                    <strong>Loan Documents</strong>
                  </a>{' '}
                and are electronically signing every document contained therein
                that requires your signature.
              </p>
            </div>
            <Button
              className={cn(style.acknowledgeBtn, { [style.acknowledged]: isChecked })}
              onClick={handleCheckboxChange}
            >
              <span className={cn(style.checkCircle, { [style.checked]: isChecked })}>
                {isChecked && (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M2.5 7L5.5 10L11.5 4" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </span>
              ACKNOWLEDGE
            </Button>
            <Button
              className={cn('button large green w-100', style.esignBtn)}
              onClick={handleEsign}
              disabled={!isChecked || loading}
            >
              {loading ? 'Loading....' : 'ESIGN LOAN DOCUMENTS'}
            </Button>
            <Row className={style.loanAgreementWrapper}>
              <Col
                className={style.agreement}
                sm={{ size: 12 }}
                md={{ size: 10 }}
              >
                <div className={style.agreementHeader}>
                  <p>Loan Agreement</p>
                  <Button
                    className={cn(
                    'large arrow buttonStyle',
                    style.button,
                    style.fullscreenButton
                  )}
                    onClick={() => window.open(pdfUrl)}
                    color="primary"
                  >
                    <img
                      src={fullScreenIcon}
                      alt="Fullscreen"
                      style={{ width: 20 }}
                    />
                  </Button>
                </div>
                <div className={style['pdf-wrapper']} ref={pdfContainerRef} />
              </Col>
              <Col sm={{ size: 12 }} md={{ size: 2 }}>
                <div className={style.features}>
                  <div>
                    <span>
                      <img src={easyIcon} alt="EasyIcon" />
                    </span>
                    <p>
                    We&apos;ve Made
                    <br /> This Easy
                    </p>
                  </div>
                  <div>
                    <span>
                      <img src={superfastIcon} alt="SuperfastIcon" />
                    </span>
                    <p>
                    Super Fast
                    <br /> E-Sign
                    </p>
                  </div>
                  <div>
                    <span>
                      <img src={stepsIcon} alt="StepsIcon" />
                    </span>
                    <p>
                    Here To Help
                    <br /> At Each Step
                    </p>
                  </div>
                </div>
              </Col>
            </Row>
              </Fragment>
            }
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default EsignLoanDocs;
